import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Trash2, Send, RefreshCw, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PendingSubscriptionsPage = () => {
  const [pendingSubscriptions, setPendingSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPendingSubscriptions();
  }, []);

  const loadPendingSubscriptions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api/admin/pending-subscriptions`, {
        params: { token }
      });
      setPendingSubscriptions(response.data.subscriptions);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des abonnements en attente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriptionId, customerName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'abonnement de ${customerName} ?\n\nCela supprimera également le client et le véhicule associés.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/api/admin/pending-subscriptions/${subscriptionId}`, {
        params: { token }
      });
      setSuccess('Abonnement supprimé avec succès');
      loadPendingSubscriptions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error(err);
    }
  };

  const handleResendPaymentLink = async (subscriptionId, customerEmail) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/api/admin/pending-subscriptions/${subscriptionId}/resend-payment-link`,
        {},
        { params: { token } }
      );
      
      // Copy link to clipboard
      await navigator.clipboard.writeText(response.data.checkout_url);
      setSuccess(`Lien de paiement copié ! Envoyez-le à ${customerEmail}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Erreur lors de la génération du lien');
      console.error(err);
    }
  };

  const handleCleanupOld = async (days) => {
    if (!window.confirm(`Supprimer tous les abonnements en attente depuis plus de ${days} jours ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(`${API}/api/admin/pending-subscriptions/cleanup-old`, {
        params: { token, days }
      });
      setSuccess(`${response.data.deleted_count} abonnement(s) supprimé(s)`);
      loadPendingSubscriptions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur lors du nettoyage');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    let timeAgo = '';
    if (diffDays > 0) {
      timeAgo = `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      timeAgo = `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else {
      timeAgo = "il y a moins d'1 heure";
    }
    
    return {
      formatted: date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      timeAgo
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-[#52a675]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          📊 Abonnements en attente de paiement
        </h1>
        <p className="text-gray-600">
          Gérez les abonnements créés mais dont le paiement n'a pas été finalisé
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          variant="outline"
          onClick={() => handleCleanupOld(7)}
          className="gap-2"
        >
          🧹 Nettoyer &gt; 7 jours
        </Button>
        <Button
          variant="outline"
          onClick={() => handleCleanupOld(30)}
          className="gap-2"
        >
          🧹 Nettoyer &gt; 30 jours
        </Button>
        <Button
          variant="outline"
          onClick={loadPendingSubscriptions}
          className="gap-2 ml-auto"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#52a675]">
              {pendingSubscriptions.length}
            </div>
            <div className="text-gray-600 mt-1">
              abonnement{pendingSubscriptions.length > 1 ? 's' : ''} en attente
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {pendingSubscriptions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">✅ Aucun abonnement en attente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingSubscriptions.map((sub) => {
            const dateInfo = formatDate(sub.created_at);
            const customer = sub.customer || {};
            const vehicle = sub.vehicle || {};
            
            return (
              <Card key={sub.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Customer info */}
                      <div className="mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {customer.first_name} {customer.last_name}
                        </h3>
                        <p className="text-sm text-gray-600">{customer.email}</p>
                      </div>

                      {/* Subscription details */}
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-sm text-gray-600">Véhicule</p>
                          <p className="font-medium">
                            {vehicle.brand} {vehicle.model} ({vehicle.year})
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Plan</p>
                          <p className="font-medium">
                            Plan {sub.plan_code} - CHF {sub.monthly_price.toFixed(2)}/mois
                          </p>
                        </div>
                      </div>

                      {/* Date */}
                      <div className="text-sm text-gray-500">
                        Créé le: {dateInfo.formatted} ({dateInfo.timeAgo})
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 sm:ml-4 sm:flex-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendPaymentLink(sub.id, customer.email)}
                        className="gap-2 flex-1 sm:flex-none"
                      >
                        <Send className="h-4 w-4" />
                        Renvoyer lien
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(sub.id, `${customer.first_name} ${customer.last_name}`)}
                        className="gap-2 flex-1 sm:flex-none"
                      >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PendingSubscriptionsPage;
